<template lang="html">
    <div class="d3-chart-wrap hydrologic-variability">
        <div
            class="svg-wrap"
        >
            <svg class="d3-chart">
                <g
                    class="chart-elements"
                    :transform="`translate(${margin.left}, ${margin.top})`"
                >
                    <g
                        v-if="initialized"
                        class="monthly-bars"
                    >
                        <g
                            v-for="(month, monthIndex) in chartData"
                            :key="monthIndex"
                            class="month"
                            :transform="`translate(${xScaleMonths(monthIndex.toString())},${0})`"
                        >
                            <!-- "outer percentiles" bar: 90th-10th -->
                            <rect
                                v-for="(period, periodKey, periodIndex) in month"
                                :key="`outer-pct${monthIndex}.${periodKey}`"
                                :class="[
                                    'percentile-outer',
                                    `period-outer-${periodIndex + adjustIndexForCandidates}`,
                                ]"
                                :x="xScalePeriods(periodKey)"
                                :y="yScaleLinear(period['90th'])"
                                :height="yScaleLinear(period['10th']) - yScaleLinear(period['90th'])"
                                :width="xScalePeriods.bandwidth()"
                            />
                            <!-- "inner percentiles" bar: 75th-25th -->
                            <rect
                                v-for="(period, periodKey, periodIndex) in month"
                                :key="`inner-pct${monthIndex}.${periodKey}`"
                                :class="[
                                    'percentile-inner',
                                    `period-inner-${periodIndex + adjustIndexForCandidates}`,
                                ]"
                                :x="xScalePeriods(periodKey)"
                                :y="yScaleLinear(period['75th'])"
                                :height="yScaleLinear(period['25th']) - yScaleLinear(period['75th'])"
                                :width="xScalePeriods.bandwidth()"
                            />

                            <!-- add monthly mean lines for each month segment -->
                            <line
                                v-for="line in meanData"
                                :key="line.name"
                                :class="[
                                    `line-${line.name}`,
                                ]"
                                fill="none"
                                :stroke="line.color"
                                :stroke-width="2"
                                :x1="0"
                                :x2="xScaleMonths.bandwidth()"
                                :y1="yScaleLinear(line.data[monthIndex])"
                                :y2="yScaleLinear(line.data[monthIndex])"
                                :width="chartWidth"
                            />
                        </g>
                        <!-- add dashed MAD lines to chart -->
                        <line
                            v-for="line in madData"
                            :key="line.name"
                            :class="[
                                `line-${line.name}`,
                            ]"
                            fill="none"
                            :stroke="line.color"
                            :stroke-width="2"
                            stroke-dasharray="10, 2"
                            :x1="0"
                            :x2="chartWidth - margin.right"
                            :y1="yScaleLinear(line.data)"
                            :y2="yScaleLinear(line.data)"
                            :width="chartWidth"
                        />
                    </g>
                    <!-- draw a border in front of everything else -->
                    <rect
                        class="chart-border"
                        x="0"
                        y="0"
                        :width="chartWidth"
                        :height="chartHeight"
                    />
                </g>
            </svg>
        </div>
    </div>
</template>

<script>
import * as d3 from 'd3';

import scssVars from '@/assets/scss/_variables.scss';

export default {
    name: 'HydrologicVariabilityChart',

    props: {
        data: {
            type: Object,
            required: true,
        },

        hasCandidates: {
            type: Boolean,
            required: false,
            default: false,
        },

        madData: {
            type: Array,
            required: true,
        },

        meanData: {
            type: Array,
            required: true,
        },

        pdfLayout: {
            type: Boolean,
            required: false,
            default: false,
        },

        width: {
            type: Number,
            required: true,
        },

        height: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        // don't render chart elements until the scales are defined
        initialized: false,

        // layout stuff
        svgWrap: null, // svg wrapper div (same size/position)
        svgEl: null,
        svg: null,
        margin: {
            top: 10, right: 3, bottom: 30, left: 55,
        },

        chartHeight: 400,
        chartWidth: 400,
        dataMin: 0,

        // scales
        xScaleMonths: null,
        xScalePeriods: null,
        yScaleLinear: null,

        // axes
        xAxis: null,
        yAxis: null,

        smallScreen: false,
    }),

    computed: {
        // return the highest [90th %ile] value in the data
        dataMax() {
            return this.chartData.reduce((acc, curr) => {
                const periods = Object.values(curr);
                const maxPercentiles = periods.map((period) => period['90th']);
                return d3.max([acc, ...maxPercentiles]);
            }, -Infinity);
        },

        // formatted data for d3
        chartData() {
            if (this.smallScreen) {
                return this.formatData({ data: this.data['Candidate 1'] });
            }
            return this.formatData(this.data);
        },

        // change color scheme to ignore first color if candidates are present
        adjustIndexForCandidates() {
            return this.hasCandidates ? 1 : 0;
        },
    },

    watch: {
        width() {
            this.smallScreen = !this.pdfLayout && (window.innerWidth < parseInt(scssVars.desktopBreakpoint, 10));
            this.init();
        },

        height() {
            this.init();
        },
    },

    mounted() {
        this.svgWrap = this.$el.querySelector('.svg-wrap');
        this.svgEl = this.svgWrap.querySelector('svg');
        this.svg = d3.select(this.svgEl);

        // define scales and call first render
        this.init();
    },

    methods: {
        /**
         * Initialize chart: define scales and axes based on element size then do an initial render
         */
        init() {
            this.initialized = false;

            // define chart margins/dimensions
            this.chartWidth = this.width - this.margin.left - this.margin.right;
            this.chartHeight = this.height - this.margin.top - this.margin.bottom;

            if (this.chartWidth < 0) {
                this.chartWidth = 0;
            }
            if (this.chartHeight < 0) {
                this.chartHeight = 0;
            }

            const months = Object.keys(this.chartData);
            const periods = Object.keys(this.data);

            // 12-month ordinal scale (for groups of bars)
            this.xScaleMonths = d3.scaleBand()
                .range([0, this.chartWidth])
                .paddingInner(0.0)
                .paddingOuter(0.0)
                .domain(months);

            // internal ordinal scale within monthly bar groups (for year ranges)
            if (this.smallScreen) {
                this.xScalePeriods = d3.scaleBand()
                    .rangeRound([0, this.xScaleMonths.bandwidth()])
                    .paddingInner(0)
                    .paddingOuter(0.15)
                    .domain(['data']);
            } else {
                this.xScalePeriods = d3.scaleBand()
                    .rangeRound([0, this.xScaleMonths.bandwidth()])
                    .paddingInner(0.25)
                    .paddingOuter(0.4)
                    .domain(periods);
            }

            // y-scale for data values
            this.yScaleLinear = d3.scaleLinear()
                .domain([this.dataMin, this.dataMax])
                .nice()
                .range([this.chartHeight, 0]);

            // use x scale to set bottom axis
            this.xAxis = d3.axisBottom(this.xScaleMonths)
                .ticks(11)
                .tickFormat((d) => {
                    // create a temporary date object to get the month
                    const date = new Date(2019, d, 1);
                    return d3.timeFormat('%b')(date);
                })
                .tickSize(this.chartHeight);

            // use Y scale to set left axis
            this.yAxis = d3.axisLeft(this.yScaleLinear)
                .ticks(6)
                .tickSize(-this.chartWidth);

            this.initialized = true;

            // add axes to chart
            this.render();
        },

        /**
         * Render or re-render the chart: add axes after it's initialized and the scales/etc. are defined
         */
        render() {
            this.g = this.svg.select('g.chart-elements');

            // clear axes and append new ones with the latest scaling
            this.g.selectAll('.axis').remove();
            this.g.append('g')
                .attr('class', 'y axis')
                .call((g) => {
                    g.call(this.yAxis);
                    g.select('.domain').remove();
                    g.selectAll('.tick line').attr('opacity', '0');
                });

            // Y axis label
            this.g.selectAll('.label').remove();
            this.g.append('text')
                .attr('class', 'y label')
                .attr('text-anchor', 'middle')
                .attr('y', 0 - this.margin.left)
                .attr('x', 0 - (this.chartHeight / 2))
                .attr('dy', '1em')
                .attr('transform', 'rotate(-90)')
                .text('Streamflow (m³/s)');

            this.g.append('g')
                .attr('class', 'x axis')
                .call((g) => {
                    g.call(this.xAxis);
                    g.select('.domain').remove();
                    // move ticks to the right, to display between months, and hide the last tick
                    const tickPosition = (this.xScaleMonths('1') - this.xScaleMonths('0')) / 2;
                    g.selectAll('.tick line').attr('transform', `translate(${tickPosition}, 0)`);
                    g.selectAll('.tick:last-of-type line').attr('opacity', 0);
                });
        },

        /**
         * Format input data for use with d3
         *
         * @param {object} rawData - data from the API endpoint (tabular)
         * @returns {Array} - restructured data for chart
         */
        formatData(rawData) {
            /*
            format data for d3

            data structure:
            -> months 0-11
            -> -> "periods" (year ranges)
            -> -> -> percentiles (2 bars' worth)
            */
            const formatted = [];
            const periods = Object.keys(rawData);
            const percentiles = [
                '10th',
                '25th',
                '75th',
                '90th',
            ];

            // build data structure by month
            for (let month = 1; month <= 12; month += 1) {
                const monthIndex = month - 1;

                // build the structure if it's not defined yet
                if (!formatted[monthIndex]) {
                    formatted[monthIndex] = {};
                    periods.forEach((period) => {
                        formatted[monthIndex][period] = {};
                    });
                }

                // traverse each year range period
                periods.forEach((period) => {
                    const periodData = rawData[period];

                    // traverse each percentile value in the year range period
                    percentiles.forEach((percentile) => {
                        const percentileData = periodData[percentile];

                        formatted[monthIndex][period][percentile] = percentileData[month];
                    });
                });
            }

            return formatted;
        },
    },
};
</script>

<style lang="scss">
.hydrologic-variability {
    $chart-border-color: $grey-lighter;
    $chart-border-width: 1px;

    &.d3-chart-wrap {
        height: 100%;
        width: 100%;

        .svg-wrap {
            height: 100%;
            width: 100%;

            text.y.label,
            .x > .tick > text,
            .y > .tick > text {
                font-weight: 300;
                font-family: $family-primary;
            }

            > svg {
                height: 100%;
                width: 100%;
            }

            .chart-border {
                fill: none;
                stroke-width: $chart-border-width;
                stroke: $chart-border-color;
                shape-rendering: crispEdges;
            }

            .axis {
                &.x {
                    .tick line {
                        shape-rendering: crispEdges;
                        stroke: $chart-border-color;
                        stroke-width: $chart-border-width;
                    }
                }
            }

            // define fill colors for chart bars
            .period-inner-0 { fill: $color-hydrovar-1; }
            .period-outer-0 { fill: $color-hydrovar-1-accent; }
            .period-inner-1 { fill: $color-hydrovar-2; }
            .period-outer-1 { fill: $color-hydrovar-2-accent; }
            .period-inner-2 { fill: $color-hydrovar-3; }
            .period-outer-2 { fill: $color-hydrovar-3-accent; }
            .period-inner-3 { fill: $color-hydrovar-4; }
            .period-outer-3 { fill: $color-hydrovar-4-accent; }
        }
    }

    @media screen and (max-width: $desktop) {
        &.d3-chart-wrap {
            flex-direction: column;
        }
    }
}
</style>
